import { useEffect, useCallback, useMemo } from 'react';
import { generateClient } from '@aws-amplify/api';
import { v4 as uuidv4 } from 'uuid';
import { LangflowStatus } from '@alucio/aws-beacon-amplify/src/API';
import { aiSearch } from '@alucio/aws-beacon-amplify/src/graphql/queries';
import {
  useDocumentSearchV2Context,
  AIScientificSummary,
  AIScientificSummaryParagraph,
  ReferenceMetadata,
} from 'src/hooks/useDocumentSearchV2';
import { useCurrentUser } from '../../../../state/redux/selector/user';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import useCurrentPage from 'src/components/DNA/hooks/useCurrentPage';
import loggers from 'src/utils/logger';
import { getAISearchFlow, getAISearchIndexName } from 'src/types/aiSearchFlows';
import { buildAISearchHistoryEntry, recordAISearchHistory } from 'src/utils/aiSearchHistory';

export { AI_SEARCH_FLOW_ID } from 'src/types/aiSearchFlows';

export interface AISearchResultsProps {
  searchText: string;
}

export interface LangflowResponse {
  tenantId: string;
  userId: string;
  sessionId: string;
  status: LangflowStatus;
  statusDetail?: string | null;
  response?: string | null;
  metadata?: string | null;
}

export type LangflowSubscription = { unsubscribe: () => void };

const generateSessionId = (userId: string) => {
  const timestamp = new Date().toISOString();
  return `${userId}_${timestamp}_${uuidv4()}`;
};

const AI_SEARCH_STATUS_FILTERS = {
  VIEWER: ['PUBLISHED'],
  PUBLISHER: ['NOT_PUBLISHED', 'PUBLISHED', 'REVOKED', 'ARCHIVED'],
} as const;

export const getAISearchStatusFilters = (isPublisherRoute: boolean): string[] => (
  isPublisherRoute
    ? [...AI_SEARCH_STATUS_FILTERS.PUBLISHER]
    : [...AI_SEARCH_STATUS_FILTERS.VIEWER]
);

export const buildAISearchPayload = (
  searchText: string,
  tenantId: string,
  statusFilters: string[],
  enableQueryCleaning = false,
) => ({
  query: searchText,
  tenant_id: tenantId,
  filters: statusFilters,
  enable_query_cleaning: enableQueryCleaning,
});

const isAIScientificSummary = (value: unknown): value is AIScientificSummary => (
  !!value &&
  typeof value === 'object' &&
  'scientific_summary' in value &&
  !!(value as AIScientificSummary).scientific_summary?.header &&
  Array.isArray((value as AIScientificSummary).scientific_summary?.body_paragraphs)
);

const getNestedResponse = (value: unknown): unknown => {
  if (!value || typeof value !== 'object') {
    return undefined;
  }

  const responseValue = value as {
    onLangflowResponse?: { response?: unknown };
    response?: unknown;
  };

  return responseValue.onLangflowResponse?.response ?? responseValue.response;
};

const EMPTY_AI_SUMMARY_TEXTS = new Set([
  'ai-generated result',
]);

const normalizeSummaryText = (text: string): string => (
  text
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\.+$/, '')
    .toLowerCase()
);

const hasMeaningfulSummaryText = (text?: string | null): text is string => (
  !!text?.trim() && !EMPTY_AI_SUMMARY_TEXTS.has(normalizeSummaryText(text))
);

export const parseAISearchResponse = (
  response: unknown,
): AIScientificSummary | null => {
  let parsedResponse = response;

  for (let attempts = 0; attempts < 5; attempts += 1) {
    if (isAIScientificSummary(parsedResponse)) {
      return parsedResponse;
    }

    const nestedResponse = getNestedResponse(parsedResponse);
    if (nestedResponse !== undefined) {
      parsedResponse = nestedResponse;
      continue;
    }

    if (typeof parsedResponse !== 'string') {
      return null;
    }

    try {
      parsedResponse = JSON.parse(parsedResponse);
    } catch (_error) {
      return null;
    }
  }

  return isAIScientificSummary(parsedResponse) ? parsedResponse : null;
};

export const getNoAnswerParagraphs = (
  report: AIScientificSummary | null,
): AIScientificSummaryParagraph[] => (
  report?.scientific_summary?.answer_found === false
    ? (report.scientific_summary.body_paragraphs ?? [])
      .filter((paragraph) => hasMeaningfulSummaryText(paragraph.narrative_text))
    : []
);

export const getSummaryParagraphs = (
  report: AIScientificSummary,
): AIScientificSummaryParagraph[] => {
  if (report.scientific_summary?.answer_found === false) {
    return [];
  }

  const bodyParagraphs = (report.scientific_summary?.body_paragraphs ?? [])
    .filter((paragraph) => hasMeaningfulSummaryText(paragraph.narrative_text));
  const messagingDocuments = report.scientific_summary?.messaging_documents;

  if (
    !messagingDocuments?.included ||
    !hasMeaningfulSummaryText(messagingDocuments.narrative_text) ||
    !messagingDocuments.citations?.length
  ) {
    return bodyParagraphs;
  }

  return [
    ...bodyParagraphs,
    {
      narrative_text: messagingDocuments.narrative_text,
      citations: messagingDocuments.citations ?? [],
    },
  ];
};

export const hasSummaryContent = (
  report: AIScientificSummary | null,
): boolean => (report ? getSummaryParagraphs(report).length > 0 : false);

export const shouldShowSummaryHeader = (
  report: AIScientificSummary | null,
): boolean => hasSummaryContent(report);

type AIReferenceDocumentMap = Record<string, { model?: { title?: string | null } } | undefined>;

export const collectAISearchReferences = (
  parsedReport: AIScientificSummary,
  allDocVersions: AIReferenceDocumentMap,
): ReferenceMetadata[] => {
  if (!hasSummaryContent(parsedReport)) {
    return [];
  }

  const seenIds = new Set<string>();
  const messagingDocs: ReferenceMetadata[] = [];
  const normalDocs: ReferenceMetadata[] = [];

  const normalizePageNumber = (page: string | number | undefined): string | undefined =>
    page != null ? String(page) : undefined;

  const messagingDocuments = parsedReport.scientific_summary?.messaging_documents;
  if (
    messagingDocuments?.included &&
    hasMeaningfulSummaryText(messagingDocuments.narrative_text) &&
    messagingDocuments.citations?.length
  ) {
    messagingDocuments.citations.forEach((citation) => {
      const pageStr = normalizePageNumber(citation.page_number);
      const citationKey = `${citation.document_version_id}_${pageStr || ''}`;
      if (citation.document_version_id && !seenIds.has(citationKey)) {
        seenIds.add(citationKey);

        let title = citation.source_name;
        if (!title) {
          const docVer = allDocVersions[citation.document_version_id];
          title = docVer?.model?.title || citation.document_version_id;
        }

        messagingDocs.push({
          id: citation.document_version_id,
          title,
          page_number: pageStr,
          text: messagingDocuments.narrative_text,
          quotedText: citation.exact_quoted_text,
          isMessaging: true,
        });
      }
    });
  }

  (parsedReport.scientific_summary?.body_paragraphs ?? [])
    .filter((paragraph) => hasMeaningfulSummaryText(paragraph.narrative_text))
    .forEach((paragraph) => {
      paragraph.citations?.forEach((citation) => {
        const pageStr = normalizePageNumber(citation.page_number);
        const citationKey = `${citation.document_version_id}_${pageStr || ''}`;
        if (citation.document_version_id && !seenIds.has(citationKey)) {
          seenIds.add(citationKey);

          let title = citation.source_name;
          if (!title) {
            const docVer = allDocVersions[citation.document_version_id];
            title = docVer?.model?.title || citation.document_version_id;
          }

          normalDocs.push({
            id: citation.document_version_id,
            title,
            page_number: pageStr,
            text: paragraph.narrative_text,
            quotedText: citation.exact_quoted_text,
          });
        }
      });
    });

  return [...messagingDocs, ...normalDocs];
};

export const getTenantAIDisclaimer = (
  config?: { aiSearchDisclaimer?: string | null } | null,
): string | undefined => {
  const disclaimer = config?.aiSearchDisclaimer;

  return disclaimer?.trim() ? disclaimer : undefined;
};

export const useAIReferences = () => {
  const { aiSearchSessionData, dispatchAiSearch, allDocVersions } = useDocumentSearchV2Context();
  const { references } = aiSearchSessionData;

  const fetchReferences = useCallback(async (parsedReport: AIScientificSummary) => {
    dispatchAiSearch({
      type: 'SET_REFERENCES',
      payload: collectAISearchReferences(parsedReport, allDocVersions),
    });
  }, [dispatchAiSearch, allDocVersions]);

  return {
    references,
    fetchReferences,
  };
};

export const useAISearchBase = () => {
  const { aiSearchSessionData, dispatchAiSearch, aiSearchFlowId } = useDocumentSearchV2Context();
  const {
    report,
    references,
    sessionId,
    loading,
    statusMessage,
    error,
    searchText: activeSearchText,
  } = aiSearchSessionData;

  const currentUser = useCurrentUser();
  const userId = currentUser.userProfile?.id;
  const tenantId = currentUser.relations?.tenant?.id;

  const { fetchReferences } = useAIReferences();

  return {
    aiSearchSessionData,
    aiSearchFlowId,
    dispatchAiSearch,
    report,
    references,
    sessionId,
    loading,
    statusMessage,
    error,
    activeSearchText,
    userId,
    tenantId,
    fetchReferences,
  };
};

export const useAISearch = ({
  searchText,
}: Pick<AISearchResultsProps, 'searchText'>) => {
  const {
    aiSearchFlowId,
    dispatchAiSearch,
    loading,
    error,
    report,
    references,
    statusMessage,
    activeSearchText,
    userId,
    tenantId,
  } = useAISearchBase();
  const route = useCurrentPage();
  const isCurrentFlowEnabled = useFeatureFlags('BEAC_7776_ai_search_current_flow');
  const isDocumentSearchAgentEnabled = useFeatureFlags('BEAC_7776_document_search_agent_flow');
  const isFlowSelectorEnabled = useFeatureFlags('enableAISearchFlowSelector');
  const isLunaEnabled = useFeatureFlags('BEAC_8540_ai_search_luna');
  const isQueryCleaningEnabled = useFeatureFlags('BEAC_8317_enable_query_cleaning');
  const flow = getAISearchFlow(aiSearchFlowId, isFlowSelectorEnabled, isDocumentSearchAgentEnabled, isLunaEnabled);
  const flowId = flow.id;
  const indexName = getAISearchIndexName(flow, isCurrentFlowEnabled);
  const isPublisherRoute = !!route?.configOptions?.modules?.includes('publisher');
  const statusFilters = useMemo(
    () => getAISearchStatusFilters(isPublisherRoute),
    [isPublisherRoute],
  );

  const startSearch = useCallback(async () => {
    if (!userId || !tenantId || !searchText) return;

    const newSessionId = generateSessionId(userId);
    const request = { userId, tenantId, flowId, indexName, filters: statusFilters, startedAt: Date.now() };
    recordAISearchHistory(buildAISearchHistoryEntry(request, newSessionId, searchText, 'running'));
    dispatchAiSearch({ type: 'INITIATE_SEARCH', payload: { searchText, sessionId: newSessionId, request } });

    try {
      const client = generateClient();
      const payload = buildAISearchPayload(searchText, tenantId, statusFilters, isQueryCleaningEnabled);

      const metadata = {
        feature: 'ai-search',
        timestamp: new Date().toISOString(),
        indexName,
      };

      await client.graphql({
        query: aiSearch,
        variables: {
          input: {
            flowId,
            sessionId: newSessionId,
            payload: JSON.stringify(payload),
            metadata: JSON.stringify(metadata),
          },
        },
      });
    } catch (err) {
      recordAISearchHistory(buildAISearchHistoryEntry(
        request, newSessionId, searchText, 'error', null, [], 'Search failed to start',
      ));
      loggers.search.error('Error starting AI search:', err);
      dispatchAiSearch({ type: 'SET_ERROR', payload: 'Search failed to start' });
      dispatchAiSearch({ type: 'SET_SEARCH_TEXT', payload: '' }); // Reset to allow retry
    }
  }, [
    userId,
    tenantId,
    searchText,
    dispatchAiSearch,
    flowId,
    indexName,
    isQueryCleaningEnabled,
    statusFilters,
  ]);

  useEffect(() => {
    if (searchText && searchText === activeSearchText && !loading && !report && !error) {
      startSearch();
    }
  }, [activeSearchText, error, loading, report, searchText, startSearch]);

  return {
    loading,
    error,
    report,
    statusMessage,
    references,
    activeSearchText,
    startSearch,
    flowId,
  };
};
